const SUMMARY_REQUESTS = {
  jobs: {
    module: 'jobs',
    keys: ['jobRecruitmentStatus', 'jobStatus'],
    dateField: 'createdAt',
  },
  candidates: {
    module: 'candidates',
    keys: ['candidateSummary'],
    items: [
      {
        key: 'created',
        label: 'Created',
        collection: 'candidates',
        countType: 'documents',
        dateField: 'createdAt',
      },
      {
        key: 'submitted',
        label: 'Submitted',
        collection: 'submissions',
        countType: 'distinct',
        distinctField: 'applicantId',
        dateField: 'submittedAt',
      },
      {
        key: 'onboarded',
        label: 'Onboarded',
        collection: 'onboarding',
        countType: 'distinct',
        distinctField: 'candidateId',
        dateField: 'onboardingActivity.activityTime',
        filters: {
          onboardingObStage: ['Completed'],
        },
      },
    ],
  },
  submissions: {
    module: 'submissions',
    keys: [
      'internalSubmissionStatus.internalSubmissionStatusText',
      'clientSubmissionStatus.clientSubmissionStatusText',
    ],
    dateField: 'submittedAt',
  },
};

export const SUMMARY_VISIBLE_COUNTS = {
  jobSummary: [
    {
      label: 'Open',
      field: 'jobStatus',
      keys: ['open', 'active'],
    },
    {
      label: 'Partially Fulfilled',
      field: 'jobRecruitmentStatus',
      keys: ['partially fulfilled', 'partiallyFulfilled'],
    },
    {
      label: 'Fulfilled',
      field: 'jobRecruitmentStatus',
      keys: ['fulfilled'],
    },
    {
      label: 'In-active / Closed',
      field: 'jobStatus',
      keys: ['closed', 'inactive', 'in-active'],
    },
  ],
};

function normalizeComparableText(value) {
  return String(value ?? '').trim().toLowerCase();
}

function makeSummaryItem(label, count) {
  return {
    label,
    count: Number.isFinite(Number(count)) ? Number(count) : 0,
  };
}

function getCountsFromField(field) {
  return Array.isArray(field?.counts) ? field.counts : [];
}

function getFieldFromSummary(data, key) {
  if (data?.key === key) return data;

  const fields = Array.isArray(data?.fields) ? data.fields : [];
  return fields.find((field) => field?.key === key) ?? {};
}

function getCountByLabels(field, labels) {
  const labelList = Array.isArray(labels) ? labels : [labels].filter(Boolean);
  const normalizedLabels = labelList.map(normalizeComparableText);

  return getCountsFromField(field).reduce((total, item) => {
    const itemLabel = normalizeComparableText(item?.key ?? item?.label);
    if (!normalizedLabels.includes(itemLabel)) return total;

    return total + (Number(item?.count) || 0);
  }, 0);
}

export function getSummaryRequest(moduleName) {
  const normalized = normalizeComparableText(moduleName);

  if (normalized.includes('candidate')) return SUMMARY_REQUESTS.candidates;
  if (normalized.includes('submission')) return SUMMARY_REQUESTS.submissions;
  if (normalized.includes('job')) return SUMMARY_REQUESTS.jobs;

  return null;
}

export function visibleSummaryCountsFor(moduleName) {
  return SUMMARY_VISIBLE_COUNTS[moduleName] ?? null;
}

export function getJobSummaryItemsFromApi(data) {
  return (visibleSummaryCountsFor('jobSummary') ?? []).map((item) => {
    const field = getFieldFromSummary(data, item.field);
    return makeSummaryItem(item.label, getCountByLabels(field, item.keys ?? []));
  });
}

export function getCandidateSummaryItems(data) {
  return getCountsFromField(data).map(({ label, key, count }) =>
    makeSummaryItem(label || key, count),
  );
}

export function getSubmissionSummaryFromApi(data) {
  const fields = Array.isArray(data?.fields) ? data.fields : [];
  const clientGroup = fields.find(g => g.key === 'clientSubmissionStatus.clientSubmissionStatusText') ?? {};
  const internalGroup = fields.find(g => g.key === 'internalSubmissionStatus.internalSubmissionStatusText') ?? {};

  const toBreakdown = (g) =>
    Array.isArray(g.counts) ? g.counts.map(({ label, count }) => makeSummaryItem(label, count)) : [];

  const clientCount = Number(clientGroup.total) || 0;
  const internalCount = Number(internalGroup.total) || 0;

  return {
    total: clientCount + internalCount,
    clientCount,
    internalCount,
    clientBreakdown: toBreakdown(clientGroup),
    internalBreakdown: toBreakdown(internalGroup),
  };
}
